import i18nFactory from '@yandex-int/i18n';
import { isUndefined, noop } from 'lodash';
import { RefObject } from 'react';
import * as React from 'react';

import { PopupDecorator, PopupDirections } from '../../components/PopupDecorator/PopupDecorator';
import { PopupScopeContext } from '../../service/Dom/Dom';
import { ThemePopup } from '../../typings/common';
import { GALLERY_HOVER_PREVIEW_Z_INDEX_LEVEL, ResourceType } from '../common/typings';
import { Gallery } from '../Gallery/Gallery';
import { GalleryItem, HoverData } from '../GalleryItem/GalleryItem';
import {
    CommonGalleryProps,
    GalleryImageCollectionItem,
    GalleryPaneTypes,
    PreviewImageItemParams
} from '../GalleryPane/typings';
import {
    GalleryPaneCategoriesFilter,
    GalleryPaneCategoriesFilterProps,
    GalleryPaneNameFilter,
    GalleryPaneNameFilterProps,
    GalleryPaneOrderFilterProps
} from '../GalleryPaneFilters/GalleryPaneFilters';
import { GalleryPreviewImage } from '../GalleryPreviewImage/GalleryPreviewImage';
import { GalleryPreviewModalPopup } from '../GalleryPreviewModalPopup/GalleryPreviewModalPopup';
import { GallerySmallPreview } from '../GallerySmallPreview/GallerySmallPreview';
import { RecentOrderSelector } from '../RecentOrderSelector/RecentOrderSelector';

import * as keyset from './CanvasImageGallery.i18n';

const i18n = i18nFactory(keyset);

const SMALL_PREVIEW_POPUP_DIRECTIONS = [
    PopupDirections.RIGHT_TOP,
    PopupDirections.RIGHT_BOTTOM,
    PopupDirections.TOP_LEFT,
    PopupDirections.TOP_RIGHT,
    PopupDirections.BOTTOM_RIGHT,
    PopupDirections.BOTTOM_LEFT
];

interface CanvasImageGalleryFiltersProps
    extends GalleryPaneCategoriesFilterProps,
        GalleryPaneNameFilterProps,
        GalleryPaneOrderFilterProps {}

export interface CanvasImageGalleryProps extends CanvasImageGalleryFiltersProps, CommonGalleryProps {
    currentPaneCollection: GalleryImageCollectionItem[];

    previewImageItem: (paneType: GalleryPaneTypes, params?: PreviewImageItemParams) => void;
    hoveredItemIndex?: number;
    hoverItem: (collectionIndex?: number) => void;
}

export class CanvasImageGallery extends React.PureComponent<CanvasImageGalleryProps> {
    private currentRef: RefObject<HTMLDivElement> | null;

    render() {
        const props = {
            ...this.props,
            itemRenderer: this.itemRenderer,
            paneFilterRenderer: this.paneFilterRenderer,
            header: i18n('title'),
            resourceType: ResourceType.image
        };

        return (
            <>
                <Gallery {...props} />
                {this.renderHoveredPreviewPopup()}
                {this.renderModalPreviewPopup()}
            </>
        );
    }

    private renderModalPreviewPopup = () => {
        const { preview, isPopupOpened } = this.props;

        if (!preview || !isPopupOpened) {
            return;
        }

        const { itemSource, hasNext, hasPrev, itemName, itemIndex, error } = preview;
        const header = itemName
            ? i18n('image-preview-header-with-name', {
                  name: itemName
              })
            : i18n('image-preview-header');

        return (
            <GalleryPreviewModalPopup
                header={header}
                hasPrevious={hasPrev}
                hasNext={hasNext}
                itemCollectionIndex={itemIndex}
                previewItem={this.previewItem}
                applyItem={this.props.applyItem}
                error={error}
            >
                <GalleryPreviewImage previewSource={itemSource as string} />;
            </GalleryPreviewModalPopup>
        );
    };

    private previewItem = (itemIndex?: number): void => {
        const { previewImageItem, currentPaneType, currentPaneCollection } = this.props;
        if (isUndefined(itemIndex)) {
            previewImageItem(currentPaneType);
        } else {
            const item = currentPaneCollection[itemIndex];
            const hasPrev = !isUndefined(currentPaneCollection[itemIndex - 1]);
            const hasNext = !isUndefined(currentPaneCollection[itemIndex + 1]);

            previewImageItem(currentPaneType, {
                itemIndex,
                hasPrev,
                hasNext,
                itemSource: item.largePreviewUrl || item.previewUrl,
                itemName: item.name
            });
        }
    };

    private renderHoveredPreviewPopup = () => {
        const { hoveredItemIndex, currentPaneCollection } = this.props;

        if (isUndefined(hoveredItemIndex)) {
            return;
        }

        const item = currentPaneCollection[hoveredItemIndex];

        if (!item || !this.currentRef || !this.currentRef.current) {
            return;
        }

        return (
            <PopupScopeContext.Provider value={this.getRef}>
                <PopupDecorator
                    theme={ThemePopup.CLEAR}
                    visible={true}
                    target={'anchor'}
                    anchor={this.currentRef.current}
                    onOutsideClick={noop}
                    onClose={noop}
                    hasClose={false}
                    directions={SMALL_PREVIEW_POPUP_DIRECTIONS}
                    zIndexGroupLevel={GALLERY_HOVER_PREVIEW_Z_INDEX_LEVEL}
                >
                    <GallerySmallPreview item={item} />
                </PopupDecorator>
            </PopupScopeContext.Provider>
        );
    };

    private getRef = () => document.body;

    private getCategoriesFilterButtonText = (count: number): string => {
        return i18n('selected-categories-count', { count });
    };

    private paneFilterRenderer = (paneType: GalleryPaneTypes): React.ReactNode => {
        const {
            recentNameFilter,
            recentOrder,
            changeRecentOrder,
            changeRecentNameFilter,
            selectGalleryCategory,
            fetchGalleryCategoriesData,
            categoriesData,
            selectedGalleryCategories
        } = this.props;

        switch (paneType) {
            case GalleryPaneTypes.recent:
                return (
                    <>
                        <GalleryPaneNameFilter
                            changeRecentNameFilter={changeRecentNameFilter}
                            recentNameFilter={recentNameFilter}
                        />
                        <RecentOrderSelector change={changeRecentOrder} value={recentOrder} />
                    </>
                );

            case GalleryPaneTypes.stock:
                return (
                    <GalleryPaneCategoriesFilter
                        fetchGalleryCategoriesData={fetchGalleryCategoriesData}
                        selectGalleryCategory={selectGalleryCategory}
                        categoriesData={categoriesData}
                        selectedGalleryCategories={selectedGalleryCategories}
                        getButtonText={this.getCategoriesFilterButtonText}
                    />
                );

            case GalleryPaneTypes.idea:
            default:
                return;
        }
    };

    private itemRenderer = (index: number): React.ReactNode => {
        const { editItemName, removeItem, hoverItem, applyItem } = this.props;

        const item = this.props.currentPaneCollection[index];

        const selfHoverItem = (data?: HoverData) => {
            if (data) {
                this.currentRef = data.ref;
                hoverItem(data.index);
            } else {
                this.currentRef = null;
                hoverItem();
            }
        };

        return (
            <GalleryItem
                key={`gallery-item-${item.id}`}
                thumbnailUrl={item.thumbnailUrl}
                resourceId={item.id}
                name={item.name}
                removable={item.removable}
                applyItem={applyItem}
                removeItem={removeItem}
                previewItem={this.previewItem}
                editItemName={editItemName}
                hoverItem={selfHoverItem}
                collectionIndex={index}
            />
        );
    };
}
